import React from 'react';
import { connect, useSelector } from 'react-redux';
import { Box, Button, Typography } from '@drone-js/core';
import { StickyFooter } from 'component/common/StickyFooter/StickyFooter';
import { ActivityDetailsFooterProps, mapDispatchToProps, mapStateToProps } from 'component/page/deprecatedDetail/ActivityDetailsFooter/ActivityDetailsFooter.selectors';
import { Price } from 'component/common/Price/Price';
import { selectIsAnyOverlayVisible } from 'store/slice/overlay';
import { ActivitiesSeeOptionButtonClickEventManifestV2 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { ActivitiesSeeOptionButtonClickEventFieldsV2 } from '@agoda/analytics-messages-definition-activities/events.split';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { mapPromocodeBadgesFromPricingSummary } from 'gql/service/graph/activityService';
import ActivityCardBadge from 'component/common/Badge/ActivityCardBadge';
import { ActivityBadgeCategory } from 'component/common/Badge/BadgeService.types';
import { ExperimentId } from 'core/type/ExperimentId';
import Experiment from 'component/common/Experiment/Experiment';

const ActivityDetailsFooter: React.FC<ActivityDetailsFooterProps> = (props) => {
    const { isPrerender, isSchedulesEmpty, cms, isImproveUnavailableOffer, experiments } = props;
    const buttonElementName =
        isSchedulesEmpty && !isPrerender ? (isImproveUnavailableOffer ? 'activities-search-other-dates-button' : 'activities-sold-out-button') : 'activities-see-option-button';
    const buttonText = isSchedulesEmpty && !isPrerender ? (isImproveUnavailableOffer ? cms[343877] : cms[178143]) : cms[126856];
    const isAnyOverlayVisible = useSelector(selectIsAnyOverlayVisible);
    const seeOptionsButtonRef = React.useRef<HTMLButtonElement>(null);
    const pricingSummary = props.cheapestPricingSummary || props.pricingSummary;

    const isLoading = props.isLoading || isPrerender;
    // eslint-disable-next-line no-nested-ternary
    const buttonState = isImproveUnavailableOffer
        ? isLoading
            ? 'loading'
            : props.isDisabled
              ? 'disabled'
              : 'enabled'
        : props.isDisabled
          ? 'disabled'
          : isLoading
            ? 'loading'
            : 'enabled';

    const promocodeBadges = mapPromocodeBadgesFromPricingSummary(pricingSummary, experiments);

    const handleSeeOptionsButtonClick = () => {
        trackElementEvent<ActivitiesSeeOptionButtonClickEventFieldsV2>(ActivitiesSeeOptionButtonClickEventManifestV2.ids, {});
        props.onSeeOptionsButtonClick(props.packageOptionsHref);
        seeOptionsButtonRef.current?.blur();
    };

    return (
        <StickyFooter elementName="details-sticky-footer" ariaHidden={isAnyOverlayVisible} zIndex={3}>
            <div className="ContentArea">
                <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                    <Box display="flex" flexDirection="column" px="8">
                        <Box display="flex" flexDirection="column" data-testid="available-detail-price">
                            <Price isSchedulesEmpty={isSchedulesEmpty} pricingSummary={pricingSummary} priceDirection="column" finalPriceSize="l" showPointsBadge={false} />
                            {props.showInclusiveTax && (
                                <Typography variant="caption-default" textColor="base.subtle">
                                    {cms[319651]}
                                </Typography>
                            )}
                            {props.cancellationMessage && (
                                <Box display="flex">
                                    <Experiment.WhenA id={ExperimentId.ADSEFF_309}>
                                        <Typography tag="span" variant="body-s-strong" textColor="deprecated.primary.easycancel">
                                            {props.cancellationMessage}
                                        </Typography>
                                    </Experiment.WhenA>
                                    <Experiment.WhenB id={ExperimentId.ADSEFF_309}>
                                        <Typography tag="span" variant="body-s-strong" textColor="feedback.positive">
                                            {props.cancellationMessage}
                                        </Typography>
                                    </Experiment.WhenB>
                                </Box>
                            )}
                        </Box>
                        {!isLoading && promocodeBadges.length > 0 && (
                            <Box display="flex" flexDirection="row" pt="4" data-testid="activity-promocodes-badges-container">
                                <ActivityCardBadge badges={promocodeBadges} activityId={props.activityId} badgeCategory={ActivityBadgeCategory.PROMOCODE} isBVariant={true} />
                            </Box>
                        )}
                    </Box>
                    <Box display="flex" flexDirection="column" width="50%" px="8">
                        <Button
                            label={isLoading ? '' : buttonText}
                            data-testid={buttonElementName}
                            data-element-name={buttonElementName}
                            ref={seeOptionsButtonRef}
                            onClick={handleSeeOptionsButtonClick}
                            disabled={props.isDisabled}
                            state={buttonState}
                            width="parent-fit"
                            variant="label"
                            onFocus={() => (isLoading ? seeOptionsButtonRef.current?.blur() : seeOptionsButtonRef.current?.focus())}
                        />
                    </Box>
                </Box>
            </div>
        </StickyFooter>
    );
};
ActivityDetailsFooter.displayName = 'ActivityDetailsFooter';

export default connect(mapStateToProps, mapDispatchToProps)(ActivityDetailsFooter);
